<template>
  <v-card-text>
    <v-stepper v-model="form.stepper" style="box-shadow: none !important">
      <v-stepper-items>
        <v-stepper-content step="1" style="padding: 0 !important;">
          <v-form ref="emailForm" @submit.prevent="firstStep()">
            <v-text-field
                prepend-icon="mdi-email"
                v-model="form.createEmail"
                :rules="rules.emailRules"
                v-bind:label="$t('loginForm.email')"
                persistent-placeholder
                required
                type="email"
                data-cy="register-createEmail"
                autocomplete="email"
            ></v-text-field>
          </v-form>
          <v-btn color="primary" block @click="firstStep()" class="mt-2" data-cy="register-continue">
            Weiter
          </v-btn>
        </v-stepper-content>
        <v-stepper-content step="2" style="padding: 0 !important;">
          <v-form ref="passForm" lazy @submit.prevent="register()">
            <v-text-field
                prepend-icon="mdi-lock"
                v-model="form.createPassword"
                :rules="rules.passRules"
                :type="form.showPassword ? 'text' : 'password'"
                v-bind:label="$t('loginForm.password')"
                persistent-placeholder
                v-bind:hint="$t('registerForm.passwordHint')"
                required
                data-cy="register-createPassword"
                autocomplete="new-password"
            >
                <template v-slot:append>
                  <passwort-auge :sichtbar="form.showPassword" @umschalten="form.showPassword = !form.showPassword" />
                </template>
            </v-text-field>
            <v-text-field
                prepend-icon="mdi-lock"
                v-model="form.createPassword2"
                :rules="rules.passRules"
                :type="form.showPassword ? 'text' : 'password'"
                v-bind:label="$t('registerForm.reEnterPassword')"
                persistent-placeholder
                v-bind:hint="$t('registerForm.passwordHint')"
                required
                data-cy="register-createPassword2"
                autocomplete="new-password"
            >
                <template v-slot:append>
                  <passwort-auge :sichtbar="form.showPassword" @umschalten="form.showPassword = !form.showPassword" />
                </template>
            </v-text-field>

            <p
                v-if="form.registerPasswordWarning"
                class="error--text"
            >
              {{ $t("registerForm.passwordsNotIdentical") }}
            </p>

            <!--
              F-47: Staerkeanzeige. Ohne sie erfaehrt der Anwender erst beim
              Absenden, dass sein Passwort nicht genuegt - und probiert dann.
            -->
            <div v-if="form.createPassword" class="mb-4" data-cy="passwortStaerke">
              <v-progress-linear
                  :value="staerkeAnteil"
                  :color="passwortStaerke.farbe"
                  height="6"
                  rounded
              ></v-progress-linear>
              <div class="caption mt-1" :class="passwortStaerke.farbe + '--text'">
                {{ $t("registerForm.passwordStrength") }}: {{ passwortStaerke.text }}
              </div>
            </div>

            <!--
              F-49 (login#4): Die Zustimmung.

              Vorher gab es KEINE Checkbox. Der Knopf "Akzeptieren und Account
              Erstellen" im AGB-Dialog war die ganze Einwilligung, und der
              Dialog zeigte allein die AGB - die Datenschutzerklaerung kam
              darin nicht vor. Der Zustimmungstext lag zwar uebersetzt vor
              (registerForm.termsAndConditions), wurde aber nirgends angezeigt.

              Eine Einwilligung nach Art. 7 DSGVO muss von anderen Sachverhalten
              UNTERSCHEIDBAR sein (Abs. 2) und der Verantwortliche muss sie
              NACHWEISEN koennen (Abs. 1). Ein Knopf, der beides zugleich
              erledigt und nichts festhaelt, erfuellt keines von beidem -
              "eine Einwilligung, die nicht gesondert erklaert und nicht
              dokumentiert wird, ist im Zweifel keine".

              Deshalb zwei getrennte Haekchen mit je eigenem Verweis. Was noch
              fehlt, ist die Dokumentation auf dem Server; siehe Kommentar an
              zustimmung() weiter unten.
            -->
            <v-checkbox
                v-model="form.agbAkzeptiert"
                class="mt-0"
                dense
                hide-details="auto"
                data-cy="agbZustimmung"
                :rules="[(v) => !!v || $t('registerForm.zustimmungFehlt')]"
            >
              <template v-slot:label>
                <span class="caption">
                  Ich habe die
                  <!--
                    Der Pfad heisst /privacy-policy/ und liefert die AGB.
                    Nicht "korrigieren": /agb gibt es nicht (404), die
                    Datenschutzerklaerung liegt unter /datenschutz/.
                    Geprueft am 15.08.2026, Titel der Seite: "AGB | onDispatch".

                    DIESER KOMMENTAR STAND BIS ZUM 15.08.2026 INNERHALB DES
                    a-TAGS - zwischen Tagname und Attributen. Das ist kein
                    gueltiges Markup; eslint meldet dort
                    vue/no-parsing-error, und der Build bricht ab. Die
                    Unit-Tests liefen trotzdem durch - vitest baut nicht.
                  -->
                  <a
                      href="https://ondispatch.de/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      @click.stop
                  >{{ $t("registerForm.agbLink") }}</a>
                  gelesen und stimme ihnen zu.
                </span>
              </template>
            </v-checkbox>

            <v-checkbox
                v-model="form.datenschutzAkzeptiert"
                class="mt-2"
                dense
                hide-details="auto"
                data-cy="datenschutzZustimmung"
                :rules="[(v) => !!v || $t('registerForm.zustimmungFehlt')]"
            >
              <template v-slot:label>
                <span class="caption">
                  Ich habe die
                  <a
                      href="https://ondispatch.de/datenschutz/"
                      target="_blank"
                      rel="noopener noreferrer"
                      @click.stop
                  >{{ $t("registerForm.datenschutzLink") }}</a>
                  gelesen und bin mit der Verarbeitung meiner Daten einverstanden.
                </span>
              </template>
            </v-checkbox>
            <!--
              KEIN @click NEBEN type="submit".

              Hier stand beides: `type="submit"` UND `@click="register(false)"`.
              Ein Klick loeste damit zwei Aufrufe aus - einmal ueber den
              Klick-Horcher, einmal ueber `@submit.prevent` des Formulars.

              Bis F-49 fiel das nicht auf: der erste Aufruf kam mit
              acceptedTOS=false herein, oeffnete den AGB-Dialog und stieg
              wieder aus, der zweite genauso. Beide Male passierte nichts
              weiter. Seit die Zustimmung ueber Haekchen laeuft, NAVIGIERT der
              erste Aufruf - und der zweite navigiert an dasselbe Ziel:

                  NavigationDuplicated: Avoided redundant navigation to
                  current location: "/register"

              vue-router wirft, Cypress zaehlt das als Fehler der Anwendung,
              und der Job test-chrome wurde rot. Der Enter-Taste wegen bleibt
              `type="submit"`; das Formular ruft register() genau einmal.
            -->
            <v-btn
                class="mt-5"
                light
                large
                block
                type="submit"
                color="primary"
                :dark="isValid"
                :disabled="!isValid"
                data-cy="register-button"
            >{{
                $t($vuetify.breakpoint.smAndDown ? "registerForm.createAccountBreakpointMobile" : "registerForm.createAccount")
              }}
            </v-btn>
            <v-btn class="mt-2" color="primary" small text block @click="form.stepper--">Zurück</v-btn>
          </v-form>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
    <v-btn
        block
        data-cy="login-button"
        class="mt-5 mb-8"
        text
        color="primary"
        @click="gotoLoginPage()"
    >{{ $t("loginForm.registerPage") }}
    </v-btn>
    <!--
      HIER STAND EIN AGB-DIALOG. Er ist weg, und das ist eine Entscheidung, keine
      Aufraeumarbeit (login#6).

      Die AGB standen zweimal: auf ondispatch.de und als Conditions.vue in
      diesem Repo, rund 280 Zeilen Rechtstext. Seit F-49 verweist das
      Zustimmungshaekchen auf die Website; `$refs.terms.open()` hatte danach
      keinen Aufrufer mehr, die Kopie im Programm war unerreichbar.

      ENTSCHIEDEN AM 15.08.2026: Die Website ist die einzige Fassung.
      Terms.vue und Conditions.vue sind geloescht. Zwei Fassungen eines
      Rechtstextes gleichzuhalten ist auf Dauer fehleranfaelliger, als eine zu
      pflegen - und welche gilt, war bei einer Abweichung nicht zu beantworten.

      Der Text ist nicht verloren: Er steht in der Git-Historie und auf der
      Website. Wer ihn im Programm wiederhaben will, holt ihn dort heraus -
      und uebernimmt dann die Pflicht, beide gleich zu halten.
    -->
  </v-card-text>
</template>

<script>
import PasswortAuge from "../PasswortAuge.vue";
import emailRegex from "../../util/emailRegex";
import { neuesPasswortRegeln, staerke, STARK_AB } from "../../util/passwort";

let vm
export default {
  components: { PasswortAuge },
  name: "RegisterForm",
  data: () => ({
    rules: {
      emailRules: [
        (v) => !!v || vm.$t("generalForms.email.required"),
        (v) => emailRegex.test(v) || vm.$t("generalForms.email.invalid"),
      ],
      //F-47: Die Regeln stehen in util/passwort.js, samt Begruendung, warum
      //Laenge und nicht Zeichenklassen. Sie gelten fuer das SETZEN eines
      //Passworts - die Anmeldemaske darf sie nicht anwenden.
      passRules: neuesPasswortRegeln(),
    },
    form: {
      stepper: 1,
      showPassword: false,
      createEmail: "",
      createPassword: "",
      createPassword2: "",
      registerPasswordWarning: false,
      //F-49: zwei getrennte Einwilligungen
      agbAkzeptiert: false,
      datenschutzAkzeptiert: false,
    }
  }),
  created() {
    vm = this
  },
  computed: {
    passwortStaerke() {
      return staerke(this.form.createPassword);
    },
    staerkeAnteil() {
      //Der Balken zeigt die Laenge im Verhaeltnis zu "stark", nicht die Stufe -
      //so bewegt er sich beim Tippen und nicht in drei Spruengen
      const laenge = (this.form.createPassword || "").length;
      return Math.min(100, Math.round((laenge / STARK_AB) * 100));
    },
    isValid() {
      return !(this.form.createEmail.length === 0 ||
          this.form.createPassword.length === 0 ||
          this.form.createPassword2.length === 0);
    },
  },
  methods: {
    firstStep() {
      if (this.$refs.emailForm.validate()) {
        this.form.stepper++;
      }
    },
    /*
     * OHNE PARAMETER. Er hiess `acceptedTOS` und entschied, ob der AGB-Dialog
     * geoeffnet oder das Konto angelegt wird. Seit F-49 entscheidet das die
     * Pruefung der beiden Haekchen weiter unten, und es ruft nur noch eine
     * Stelle auf: `@submit.prevent` des Formulars.
     *
     * Zwischenstand am 15.08.2026 war `register(_ausDemAgbDialog)` mit einem
     * eslint-disable darueber. Das war eine Umgehung der Meldung, nicht ihre
     * Behebung - eslint hatte recht, der Parameter wurde nirgends gelesen.
     * (Die Meldung selbst hatte den Build zum Abbruch gebracht, waehrend die
     * Unit-Tests gruen blieben: vitest baut nicht und lintet nicht.)
     */
    register() {
      if (this.$refs.passForm.validate()) {
        //check if both passwords are the same
        this.form.registerPasswordWarning = false;

        if (
            this.form.createPassword !== this.form.createPassword2
        ) {
          this.form.registerPasswordWarning = true;
        } else {
          /*
           * F-49: Die Zustimmung steht jetzt im Formular, nicht mehr hinter
           * einem Knopf im AGB-Dialog.
           */
          if (!this.form.agbAkzeptiert || !this.form.datenschutzAkzeptiert) {
            return;
          }

          //both passwords are the same
          //send data to register page
          this.$router.push({
            name: "Register",
            params: {
              prepData: JSON.stringify({
                pwd: this.form.createPassword.trim(),
                email: this.form.createEmail.trim().toLowerCase(),
                /*
                 * Die Einwilligung wird mitgereicht, damit sie beim Anlegen des
                 * Kontos festgehalten werden kann.
                 *
                 * WAS DAMIT NOCH NICHT ERLEDIGT IST: Der Server muss sie auch
                 * SPEICHERN - Zeitpunkt, Fassung der Dokumente, und zwar so,
                 * dass sie sich Jahre spaeter belegen laesst (Art. 7 Abs. 1
                 * DSGVO). Dafuer braucht es Felder am Mandanten und eine
                 * Festlegung, welche Fassung von AGB und Datenschutzerklaerung
                 * gilt. Beides ist im Ticket vermerkt.
                 */
                zustimmung: {
                  agb: this.form.agbAkzeptiert,
                  datenschutz: this.form.datenschutzAkzeptiert,
                  zeitpunkt: new Date().toISOString(),
                },
              }),
            },
          });
        }
      }
    },
    gotoLoginPage() {
      this.$router.push({
        name: "Login",
        params: {
          mode: "login",
          redirect: 1,
        },
      });
    }
  }
}
</script>

<style scoped>

</style>
